import * as _ from 'underscore';

import { setRootAdminUiState } from '../app/actions/spaces/RootAdminUiStateActions';
import { showRouterError } from '../app/actions/spaces/RouterErrorActions';
import { appStore } from '../app/AppStore';
import { LoginUrlsWrapper } from '../app/spaces/Login/LoginUrls';
import { UrlParamKeys } from '../app/UrlParamKeys';
import { Tableau } from '../common/utilities/TableauLog';
import { MainCtrl } from './controllers/MainCtrl';
import { VizViewerCtrl } from './controllers/VizViewerCtrl';
import { FeatureFlags } from './FeatureFlags';
import { Helpers } from './helpers/Helpers';
import { L10n } from './L10n/L10n';
import { Module } from './Module';
import { GettingStartedNotifications } from './services/GettingStartedNotifications';
import { MastheadNotifications } from './services/MastheadNotifications';
import { PostLoginNotifications } from './services/PostLoginNotifications';
import { AuthLevel, ServerService } from './services/ServerService';
import { SiteUrlMatcherFactory } from './services/SiteUrlMatcherFactory';

export interface UserStateParams {
  domainName: string;
  username: string;
}

Module.instance().config(<any[]>[
  '$httpProvider',
  '$sceProvider',
  '$stateProvider',
  '$urlRouterProvider',
  'SiteUrlMatcherFactoryProvider',
  'ApiPrefix',
  (
    $httpProvider: ng.IHttpProvider,
    $sceProvider: any,
    $stateProvider: ng.ui.IStateProvider,
    $urlRouterProvider: ng.ui.IUrlRouterProvider,
    siteUrlMatcherFactory: SiteUrlMatcherFactory,
    ApiPrefix: string
  ) => {
    $stateProvider.decorator('parent', (state: any, parent: Function) => {
      // B147816. This adds a resolve to every state that waits for all pending session info requests (like a
      // switch site) to finish before continuing with the state transition. We don't want to let the
      // controllers act on stale session data.
      const getSessionInfoPromise = ['ServerService', (server: ServerService) => {
        // Swallow any errors. We just want to know when it's done.
        return server.getSessionInfoPromise().catch(angular.noop);
      }];
      const resolve = _.extend({getSessionInfoPromise : getSessionInfoPromise}, state.self.resolve);
      state.self.resolve = resolve;
      state.resolve = state.self.resolve;
      return parent(state);
    });

    function goToFatalErrorState(message: string) {
      Tableau.Logger().error(message);

      appStore.dispatch(setRootAdminUiState('fatal'));
    }

    $httpProvider.interceptors.push(['$q', '$injector', ($q, $injector) => {
      const apiRequestRegex = new RegExp('^' + ApiPrefix);
      return {
        request: (config: ng.IRequestConfig) => {
          const isGet = config.method === 'GET';
          const isInline = /^inline_/.test(config.url);
          const isApi = apiRequestRegex.test(config.url);
          if (isGet && !isInline && !isApi) {
            const isHtml = /\.html$/.test(config.url);
            const separator = (config.url.indexOf('?') >= 0) ? '&' : '?';
            config.url = config.url + separator + BuildId;
            if (L10n.language && isHtml) {
              // Only prefix the request with the language if:
              // - We have a valid language.
              // - The request is a GET for an html file.
              // - It's not a request for an inlined template.
              config.url = L10n.language + '/' + config.url;
            }
            config.url = StaticAssetsUrlPrefix + config.url;
          }
          return config;
        },
        responseError: (rejection: any) => {
          Tableau.Logger().error('http error ' + rejection.status + ' for request ' + rejection.config.url);
          if (rejection.status === 404) {
            if (rejection.config.url.indexOf('/error.html') >= 0) {
              // if we can't fetch the error page it's probably something pretty fundamental
              if (L10n.language && L10n.language !== 'en' && rejection.config.url.indexOf(L10n.language) === 0){
                Tableau.Logger().error("Your language setting '" + L10n.language + "' may be invalid.");
                // potential improvement - create a language service and just change the language to 'en' for a better experience
              }
              goToFatalErrorState('Failed to load error handling page.');
            }
            else if (rejection.config.url.indexOf('messages.') >= 0) {
              // failed to get strings for this language - it will fall back to English in ServerService but if this is english...
              if (L10n.language === 'en') {
                goToFatalErrorState('Failed to load strings for UI.');
              }
            }
          }
          return $q.reject(rejection);
        }
      };
    }]);

    // Angular does not support SCE on IE7-.
    // This browser detection matches the check that Angular performs internally.
    if (navigator.userAgent.match(/msie/i) && (<any>document).documentMode > 0 && (<any>document).documentMode < 8) {
      $sceProvider.enabled(false);
    }

    const siteUrl = (url: string) => siteUrlMatcherFactory.compile(url);

    // Create a redirect handler for a site url. See $urlRouterProvider documentation for details
    const siteUrlRedirect = (url: string) => ['$match', ($match: any) => siteUrl(url).format($match)];

    $urlRouterProvider
      .otherwise(($injector: ng.auto.IInjectorService): void => {
        // Handle nonsensical URLs
        // These flow will be handled by site/server space router when they are combined
        if ($injector.get<ServerService>('ServerService').inSession()) {
          appStore.dispatch(showRouterError('default'));
        } else {
          // If not signed in, remember the redirect so they get an error page after sign in
          const redirect = $injector.get<ng.ILocationService>('$location').url();
          LoginUrlsWrapper.navigateToSignIn({redirect});
        }
      });

    /**
     * State Configurations
     */

    // Connection Authentication
    $stateProvider
      .state('authenticate', {
        url: '/authenticate',
        template: '<tb:authenticate-connection-router/>',
      });

    // B147151. Ideally we would process and remove the saml query param earlier in the flow (i.e. run.ts), but
    // we can't change the url through angular's $location object then because that disrupts the bootstrapping
    // process. We would have to parse and modify the window.location directly, and that's a non-trivial task.
    const processAndRemoveSamlQueryParam = ['$location', 'PostLoginNotifications', (
      $location: ng.ILocationService,
      PostLoginNotifications: PostLoginNotifications
    ) => {
      const search = $location.search();
      if (_.has(search, UrlParamKeys.IsFromSaml)) {
        if (Helpers.BooleanQueryParamRegex.test(search[UrlParamKeys.IsFromSaml])) {
          PostLoginNotifications.enableNotifications();
        }
        $location.search(UrlParamKeys.IsFromSaml, null);
      }
    }];

    // Login
    $stateProvider
      .state('login', {
        abstract: true,
        template: '<div class="tb-column-flex-parent tb-fill"><tb:app-banner></tb:app-banner><div ui-view class="tb-flex-fill"></div></div>',
        onEnter: processAndRemoveSamlQueryParam
      });

    $stateProvider
      .state('login.react', {
        url: `/{space:(?:signin|error|signedOut|siteSamlSignedOut|resetPassword|requestPasswordReset)}`,
        template: '<tb:login-router />',
      })
      .state('login.react.subspace', {
        url: `/*subspace`,
      })
      // login site picker gets its own state so it doesn't interfere with non default sites
      .state('login.site', {
        url: `/site`,
        template: '<tb:login-router />',
      });

    // PKCE
    $stateProvider
      .state('pkce', {
        url: '/pkce/*path',
        template: '<tb:pkce-router />',
      });

    // Main

    $stateProvider
      .state('main', {
        abstract: true,
        url: '',
        templateUrl: 'main.html',
        data: {
          authLevel: AuthLevel.Authenticated
        },
        onEnter: processAndRemoveSamlQueryParam,
        controller: MainCtrl
      });

    $stateProvider
      .state('main.site', {
        abstract: true,
        // site url is optional. Grab it if it's there.
        url: siteUrlMatcherFactory.compile(''),
        template: '<div ui-view></div>',
        onEnter: ['PostLoginNotifications', 'GettingStartedNotifications', 'MastheadNotifications', (
          PostLoginNotifications: PostLoginNotifications,
          GettingStartedNotifications: GettingStartedNotifications,
          MastheadNotifications: MastheadNotifications
        ) => {
          // See http://mytableau/display/devft/Vizportal+Online+Notifications+Spec
          PostLoginNotifications.showNotificationsIfEnabled()
            .finally(() => GettingStartedNotifications.showNotificationsIfEnabled())
            .finally(() => MastheadNotifications.showNotificationsIfEnabled());
        }]
      });
    // Content: see contentConfig.ts

    // !!! ATTENTION !!!
    // If you changed the url scheme of user or user setting,
    // make sure to also update the user setting url generation code in:
    // \workgroup\vqlweb\scriptsharp\src\Authoring\ViewModels\AuthoringMastheadViewModel.cs

    // Viz

    if (!FeatureFlags.vizRR) {
      $stateProvider
        .state('main.site.viz', {
          url: '/views/*path',
          templateUrl: 'vizViewer.html',
          controller: VizViewerCtrl
        });
    }

    $stateProvider
      .state('main.serverreactrouter', {
          url: `/server`,
          template: '<tb:server-space-router/>', // ServerSpaceRouter.tsx via angularDirectives.ts
          abstract: false
      }).state('main.serverreactrouter.subspace', {
        url: '/*subspace'
      });
  }
]);
